import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowRight,
  CircleDollarSign,
  Loader2,
  Percent,
  PiggyBank,
  RefreshCw,
  Search,
  ShoppingCart,
  Wallet,
} from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { useAppSettings } from '../../hook/useAppSettings';
import { useFinancesApi } from '../../services/finances';
import { formatProjectStatus, type ProjectStatus } from '../../types/projects';
import type {
  FinanceOverviewResponse,
  FinanceProjectListItem,
  FinanceProjectsSortBy,
  FinanceSortDirection,
  FinanceSubprojectStatusFilter,
  FinanceStatusScope,
  FinanceWatchResponse,
} from '../../types/finances';
import { DashboardSubprojectsToWatch } from '../dashboard/DashboardSubprojectsToWatch';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Skeleton } from '../ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';

type FinanceProjectFilters = {
  statusScope: FinanceStatusScope;
  search: string;
  type: string;
  client: string;
};

type FinanceSubprojectFilters = {
  search: string;
  status: FinanceSubprojectStatusFilter;
};

type KpiCardDefinition = {
  key:
    | 'total_budget'
    | 'total_pvp'
    | 'total_cost'
    | 'gross_margin_amount'
    | 'iva_amount'
    | 'budget_difference'
    | 'open_orders_count'
    | 'extras_cost';
  title: string;
  helper: string;
  icon: typeof Wallet;
  iconClassName: string;
  iconWrapperClassName: string;
};

const STATUS_BADGE_VARIANTS: Record<
  ProjectStatus,
  'planning' | 'inProgress' | 'quoted' | 'finished'
> = {
  planificacion: 'planning',
  en_progreso: 'inProgress',
  presupuestado: 'quoted',
  finalizado: 'finished',
};

const STATUS_SCOPE_OPTIONS: Array<{ label: string; value: FinanceStatusScope }> = [
  { label: 'Activos', value: 'active' },
  { label: 'Todos', value: 'all' },
  { label: 'Finalizados', value: 'finalized' },
];

const SUBPROJECT_STATUS_OPTIONS: Array<{ label: string; value: FinanceSubprojectStatusFilter }> = [
  { label: 'Todos', value: 'all' },
  { label: 'Planificación', value: 'planificacion' },
  { label: 'En progreso', value: 'en_progreso' },
  { label: 'Presupuestado', value: 'presupuestado' },
  { label: 'Finalizado', value: 'finalizado' },
];

const PROJECT_SORT_OPTIONS: Array<{ label: string; value: FinanceProjectsSortBy }> = [
  { label: 'Margen %', value: 'gross_margin_pct' },
  { label: 'Margen bruto', value: 'gross_margin_amount' },
  { label: 'Desviación presupuestaria', value: 'budget_difference' },
  { label: 'PVP', value: 'total_pvp' },
  { label: 'Coste', value: 'total_cost' },
  { label: 'Fecha de creación', value: 'created_at' },
];

const SORT_DIRECTION_OPTIONS: Array<{ label: string; value: FinanceSortDirection }> = [
  { label: 'Descendente', value: 'desc' },
  { label: 'Ascendente', value: 'asc' },
];

const COST_BREAKDOWN_COLORS = ['#2563eb', '#f59e0b', '#ef4444'];

const KPI_CARDS: KpiCardDefinition[] = [
  {
    key: 'total_budget',
    title: 'Presupuesto total',
    helper: 'Presupuesto objetivo agregado',
    icon: PiggyBank,
    iconClassName: 'text-blue-600',
    iconWrapperClassName: 'bg-blue-100',
  },
  {
    key: 'total_pvp',
    title: 'PVP total',
    helper: 'Venta neta agregada',
    icon: CircleDollarSign,
    iconClassName: 'text-emerald-600',
    iconWrapperClassName: 'bg-emerald-100',
  },
  {
    key: 'total_cost',
    title: 'Coste total',
    helper: 'Ítems + montaje + extras',
    icon: Wallet,
    iconClassName: 'text-rose-600',
    iconWrapperClassName: 'bg-rose-100',
  },
  {
    key: 'gross_margin_amount',
    title: 'Margen bruto',
    helper: 'PVP menos coste total',
    icon: Percent,
    iconClassName: 'text-teal-600',
    iconWrapperClassName: 'bg-teal-100',
  },
  {
    key: 'iva_amount',
    title: 'IVA total',
    helper: 'Respeta IVA global e histórico',
    icon: CircleDollarSign,
    iconClassName: 'text-violet-600',
    iconWrapperClassName: 'bg-violet-100',
  },
  {
    key: 'budget_difference',
    title: 'Diferencia presupuestaria',
    helper: 'Presupuesto menos PVP',
    icon: Wallet,
    iconClassName: 'text-amber-600',
    iconWrapperClassName: 'bg-amber-100',
  },
  {
    key: 'open_orders_count',
    title: 'Pedidos abiertos',
    helper: 'Borrador, pedido, confirmado o enviado',
    icon: ShoppingCart,
    iconClassName: 'text-sky-600',
    iconWrapperClassName: 'bg-sky-100',
  },
  {
    key: 'extras_cost',
    title: 'Total extras',
    helper: 'Gastos adicionales de subproyectos',
    icon: PiggyBank,
    iconClassName: 'text-orange-600',
    iconWrapperClassName: 'bg-orange-100',
  },
];

const EMPTY_PROJECT_FILTERS: FinanceProjectFilters = {
  statusScope: 'active',
  search: '',
  type: '',
  client: '',
};

const EMPTY_SUBPROJECT_FILTERS: FinanceSubprojectFilters = {
  search: '',
  status: 'all',
};

const EMPTY_WATCH_DATA: FinanceWatchResponse = {
  cost_deviation: [],
  pending_orders: [],
};

function useDebounce<T>(value: T, delay: number) {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [delay, value]);

  return debounced;
}

function toNumber(value: string | number | null | undefined) {
  if (value == null) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function EmptyState({ description }: { description: string }) {
  return (
    <div className="flex min-h-[220px] items-center justify-center rounded-lg border border-dashed border-gray-200 px-6 text-center text-sm text-gray-500">
      {description}
    </div>
  );
}

function ChartCard({
  title,
  subtitle,
  loading,
  children,
  className = '',
  contentClassName = '',
}: {
  title: string;
  subtitle: string;
  loading: boolean;
  children: React.ReactNode;
  className?: string;
  contentClassName?: string;
}) {
  return (
    <Card className={`h-full shadow-sm ${className}`}>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <p className="text-sm text-gray-500">{subtitle}</p>
      </CardHeader>
      <CardContent className={contentClassName}>
        {loading ? <Skeleton className="h-[320px] w-full" /> : children}
      </CardContent>
    </Card>
  );
}

function FinanceProjectsTableSkeleton() {
  return (
    <div className="w-full">
      <Table className="table-fixed w-full text-sm [&_th]:box-border [&_td]:box-border">
        <TableHeader>
          <TableRow>
            {Array.from({ length: 12 }).map((_, index) => (
              <TableHead key={index} className="px-2">
                <Skeleton className="h-4 w-20" />
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {Array.from({ length: 5 }).map((_, row) => (
            <TableRow key={row}>
              {Array.from({ length: 12 }).map((__, cell) => (
                <TableCell key={`${row}-${cell}`} className="px-2">
                  <Skeleton className="h-5 w-full" />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export function Finances() {
  const navigate = useNavigate();
  const { formatCurrency, formatPercent } = useAppSettings();
  const { fetchOverview, fetchProjects, fetchWatch } = useFinancesApi();

  const [projectFilters, setProjectFilters] = useState<FinanceProjectFilters>(EMPTY_PROJECT_FILTERS);
  const [subprojectFilters, setSubprojectFilters] = useState<FinanceSubprojectFilters>(
    EMPTY_SUBPROJECT_FILTERS,
  );
  const [overview, setOverview] = useState<FinanceOverviewResponse | null>(null);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [overviewError, setOverviewError] = useState<string | null>(null);
  const [projects, setProjects] = useState<FinanceProjectListItem[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [projectsError, setProjectsError] = useState<string | null>(null);
  const [pageSize] = useState(10);
  const [offset, setOffset] = useState(0);
  const [pageInput, setPageInput] = useState('1');
  const [sortBy, setSortBy] = useState<FinanceProjectsSortBy>('gross_margin_pct');
  const [sortDirection, setSortDirection] = useState<FinanceSortDirection>('desc');
  const [totalProjects, setTotalProjects] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [watchData, setWatchData] = useState<FinanceWatchResponse>(EMPTY_WATCH_DATA);
  const [watchLoading, setWatchLoading] = useState(false);
  const [watchError, setWatchError] = useState<string | null>(null);

  const debouncedProjectSearch = useDebounce(projectFilters.search, 300);
  const debouncedSubprojectSearch = useDebounce(subprojectFilters.search, 300);

  const projectQueryFilters = useMemo(
    () => ({
      status_scope: projectFilters.statusScope,
      search: debouncedProjectSearch.trim() || undefined,
      type: projectFilters.type.trim() || undefined,
      client: projectFilters.client.trim() || undefined,
    }),
    [
      debouncedProjectSearch,
      projectFilters.client,
      projectFilters.statusScope,
      projectFilters.type,
    ],
  );

  const subprojectQueryFilters = useMemo(
    () => ({
      subproject_search: debouncedSubprojectSearch.trim() || undefined,
      subproject_status:
        subprojectFilters.status === 'all' ? undefined : subprojectFilters.status,
    }),
    [debouncedSubprojectSearch, subprojectFilters.status],
  );

  const overviewQueryFilters = useMemo(
    () => ({
      ...projectQueryFilters,
      ...subprojectQueryFilters,
    }),
    [projectQueryFilters, subprojectQueryFilters],
  );

  const loadOverview = async () => {
    setOverviewLoading(true);
    setOverviewError(null);
    try {
      const response = await fetchOverview(overviewQueryFilters);
      setOverview(response);
    } catch (error) {
      setOverviewError(error instanceof Error ? error.message : 'No pudimos cargar el resumen financiero.');
    } finally {
      setOverviewLoading(false);
    }
  };

  const loadProjects = async () => {
    setProjectsLoading(true);
    setProjectsError(null);
    try {
      const response = await fetchProjects({
        ...projectQueryFilters,
        ...subprojectQueryFilters,
        limit: pageSize,
        offset,
        sort_by: sortBy,
        sort_direction: sortDirection,
      });
      setProjects(response.items);
      setTotalProjects(response.pagination.total);
      setTotalPages(response.pagination.total_pages);
    } catch (error) {
      setProjectsError(
        error instanceof Error ? error.message : 'No pudimos cargar el ranking financiero.',
      );
    } finally {
      setProjectsLoading(false);
    }
  };

  const loadWatch = async () => {
    setWatchLoading(true);
    setWatchError(null);
    try {
      const response = await fetchWatch({
        ...projectQueryFilters,
        ...subprojectQueryFilters,
      });
      setWatchData(response);
    } catch (error) {
      setWatchError(
        error instanceof Error ? error.message : 'No pudimos cargar la vigilancia financiera.',
      );
    } finally {
      setWatchLoading(false);
    }
  };

  useEffect(() => {
    void loadOverview();
  }, [fetchOverview, overviewQueryFilters]);

  useEffect(() => {
    void loadProjects();
  }, [
    fetchProjects,
    offset,
    pageSize,
    projectQueryFilters,
    sortBy,
    sortDirection,
    subprojectQueryFilters,
  ]);

  useEffect(() => {
    void loadWatch();
  }, [fetchWatch, projectQueryFilters, subprojectQueryFilters]);

  useEffect(() => {
    setPageInput(String(Math.floor(offset / pageSize) + 1));
  }, [offset, pageSize]);

  const currentPage = Math.floor(offset / pageSize) + 1;

  const formatCurrencyValue = (
    value: string | number | null | undefined,
    options?: { maximumFractionDigits?: number },
  ) =>
    formatCurrency(value, {
      showZero: true,
      maximumFractionDigits: options?.maximumFractionDigits ?? 2,
    });

  const formatPercentValue = (value: string | number | null | undefined) => {
    if (value == null) return '—';
    return formatPercent(value, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  };

  const handleProjectFilterChange = (field: keyof FinanceProjectFilters, value: string) => {
    setProjectFilters((previous) => ({ ...previous, [field]: value }));
    setOffset(0);
  };

  const handleSubprojectFilterChange = (
    field: keyof FinanceSubprojectFilters,
    value: string,
  ) => {
    setSubprojectFilters((previous) => ({ ...previous, [field]: value }));
    setOffset(0);
  };

  const handleRefresh = async () => {
    await Promise.allSettled([loadOverview(), loadProjects(), loadWatch()]);
  };

  const handleResetFilters = () => {
    setProjectFilters(EMPTY_PROJECT_FILTERS);
    setSubprojectFilters(EMPTY_SUBPROJECT_FILTERS);
    setOffset(0);
    setSortBy('gross_margin_pct');
    setSortDirection('desc');
  };

  const commitPageInput = () => {
    const parsed = Number(pageInput);
    if (!Number.isInteger(parsed)) {
      setPageInput(String(currentPage));
      return;
    }
    const nextPage = Math.min(totalPages, Math.max(1, parsed));
    setOffset((nextPage - 1) * pageSize);
  };

  const costBreakdownData = useMemo(() => {
    if (!overview) return [];
    return [
      {
        name: 'Ítems',
        value: toNumber(overview.cost_breakdown.items_cost) ?? 0,
        color: COST_BREAKDOWN_COLORS[0],
      },
      {
        name: 'Montaje',
        value: toNumber(overview.cost_breakdown.mounting_cost) ?? 0,
        color: COST_BREAKDOWN_COLORS[1],
      },
      {
        name: 'Extras',
        value: toNumber(overview.cost_breakdown.extras_cost) ?? 0,
        color: COST_BREAKDOWN_COLORS[2],
      },
    ].filter((item) => item.value > 0);
  }, [overview]);

  const topProjectsByMargin = useMemo(
    () =>
      (overview?.projects_by_margin.top ?? []).map((item) => ({
        name: item.ref,
        marginPct: toNumber(item.gross_margin_pct) ?? 0,
        marginAmount: toNumber(item.gross_margin_amount) ?? 0,
      })),
    [overview],
  );

  const costDeviationData = useMemo(
    () =>
      (overview?.subprojects_cost_deviation ?? []).map((item) => ({
        name: item.subproject_name,
        deviationPct: toNumber(item.cost_deviation_pct) ?? 0,
      })),
    [overview],
  );

  const stateSummary = overview?.project_health_summary;

  return (
    <div className="min-w-0 space-y-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="mb-2 text-2xl text-gray-900">Finanzas</h1>
          <p className="text-gray-600">
            Analítica operativa de rentabilidad, costes, márgenes y riesgo presupuestario.
          </p>
        </div>
        <Button variant="outline" onClick={() => void handleRefresh()} disabled={overviewLoading || projectsLoading || watchLoading}>
          {overviewLoading || projectsLoading || watchLoading ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="mr-2 h-4 w-4" />
          )}
          Actualizar
        </Button>
      </div>

      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle>Filtros</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-4">
            <div className="lg:col-span-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <Input
                  value={projectFilters.search}
                  onChange={(event) => handleProjectFilterChange('search', event.target.value)}
                  placeholder="Buscar por referencia, proyecto o cliente"
                  className="pl-9"
                />
              </div>
            </div>
            <Select
              value={projectFilters.statusScope}
              onValueChange={(value) => handleProjectFilterChange('statusScope', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                {STATUS_SCOPE_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={handleResetFilters}>
              Limpiar filtros
            </Button>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Input
              value={projectFilters.type}
              onChange={(event) => handleProjectFilterChange('type', event.target.value)}
              placeholder="Filtrar por tipo de proyecto"
            />
            <Input
              value={projectFilters.client}
              onChange={(event) => handleProjectFilterChange('client', event.target.value)}
              placeholder="Filtrar por cliente"
            />
          </div>

          <div className="border-t border-gray-100 pt-4">
            <div className="mb-3">
              <p className="text-sm font-medium text-gray-900">Subproyectos</p>
              <p className="text-sm text-gray-500">
                Estos filtros recalculan toda la página usando solo los subproyectos que coincidan.
              </p>
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <Input
                  value={subprojectFilters.search}
                  onChange={(event) =>
                    handleSubprojectFilterChange('search', event.target.value)
                  }
                  placeholder="Buscar subproyecto por referencia o nombre"
                  className="pl-9"
                />
              </div>
              <Select
                value={subprojectFilters.status}
                onValueChange={(value) => handleSubprojectFilterChange('status', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Estado del subproyecto" />
                </SelectTrigger>
                <SelectContent>
                  {SUBPROJECT_STATUS_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="overview">Resumen</TabsTrigger>
          <TabsTrigger value="projects">Proyectos</TabsTrigger>
          <TabsTrigger value="watch">Vigilancia</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          {overviewError ? (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>No pudimos cargar el resumen financiero</AlertTitle>
              <AlertDescription>{overviewError}</AlertDescription>
            </Alert>
          ) : null}

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
            {KPI_CARDS.map((card) => {
              const Icon = card.icon;
              const value = !overview
                ? '—'
                : card.key === 'open_orders_count'
                  ? String(overview.summary.open_orders_count)
                  : card.key === 'extras_cost'
                    ? formatCurrencyValue(overview.cost_breakdown.extras_cost)
                    : card.key === 'gross_margin_amount'
                      ? formatCurrencyValue(overview.summary.gross_margin_amount)
                      : card.key === 'budget_difference'
                        ? formatCurrencyValue(overview.summary.budget_difference)
                        : card.key === 'iva_amount'
                          ? formatCurrencyValue(overview.summary.iva_amount)
                          : card.key === 'total_cost'
                            ? formatCurrencyValue(overview.summary.total_cost)
                            : card.key === 'total_pvp'
                              ? formatCurrencyValue(overview.summary.total_pvp)
                              : formatCurrencyValue(overview.summary.total_budget);

              return (
                <Card key={card.key} className="shadow-sm">
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between gap-3">
                      <div className="space-y-1">
                        <p className="text-sm text-gray-600">{card.title}</p>
                        {overviewLoading ? (
                          <Skeleton className="h-8 w-28" />
                        ) : (
                          <p className="text-2xl font-semibold text-gray-900">{value}</p>
                        )}
                        <p className="text-xs text-gray-500">{card.helper}</p>
                      </div>
                      <div
                        className={`flex h-11 w-11 items-center justify-center rounded-lg ${card.iconWrapperClassName}`}
                      >
                        <Icon className={`h-5 w-5 ${card.iconClassName}`} />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="grid grid-cols-1 gap-6 xl:grid-cols-12 xl:items-stretch">
            <div className="xl:col-span-5">
              <ChartCard
                title="Composición del coste"
                subtitle="Cómo se reparte el coste total entre ítems, montaje y extras."
                loading={overviewLoading}
                contentClassName="flex h-full flex-col"
              >
                {!overview || costBreakdownData.length === 0 ? (
                  <EmptyState description="No hay costes agregados para representar con los filtros actuales." />
                ) : (
                  <div className="flex h-full flex-col justify-between gap-6">
                    <ResponsiveContainer width="100%" height={280}>
                      <PieChart>
                        <Pie
                          data={costBreakdownData}
                          dataKey="value"
                          nameKey="name"
                          innerRadius={70}
                          outerRadius={110}
                          paddingAngle={3}
                        >
                          {costBreakdownData.map((entry) => (
                            <Cell key={entry.name} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(value) => [
                            formatCurrencyValue(value as number, { maximumFractionDigits: 0 }),
                            '',
                          ]}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                      {costBreakdownData.map((item) => (
                        <div key={item.name} className="flex items-center gap-2 text-sm text-gray-600">
                          <span className="h-3 w-3 rounded-full" style={{ backgroundColor: item.color }} />
                          <span>
                            {item.name}: {formatCurrencyValue(item.value, { maximumFractionDigits: 0 })}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </ChartCard>
            </div>

            <div className="xl:col-span-7">
              <ChartCard
                title="Estado de rentabilidad"
                subtitle="Lectura rápida de proyectos rentables, en riesgo y sobre presupuesto."
                loading={overviewLoading}
                contentClassName="flex h-full flex-col"
              >
                {!overview || !stateSummary ? (
                  <EmptyState description="No hay proyectos para clasificar con los filtros actuales." />
                ) : (
                  <div className="grid h-full grid-cols-1 gap-4 md:grid-cols-3">
                    <div className="flex h-full flex-col rounded-xl border border-emerald-200 bg-emerald-50 p-5">
                      <p className="text-sm text-emerald-700">Rentables</p>
                      <div className="flex flex-1 items-center justify-center">
                        <p className="text-center text-5xl font-semibold text-emerald-900">
                          {stateSummary.profitable}
                        </p>
                      </div>
                      <p className="mt-auto pt-2 text-xs text-emerald-700">
                        Margen positivo y sin desviación presupuestaria negativa.
                      </p>
                    </div>
                    <div className="flex h-full flex-col rounded-xl border border-amber-200 bg-amber-50 p-5">
                      <p className="text-sm text-amber-700">En riesgo</p>
                      <div className="flex flex-1 items-center justify-center">
                        <p className="text-center text-5xl font-semibold text-amber-900">
                          {stateSummary.at_risk}
                        </p>
                      </div>
                      <p className="mt-auto pt-2 text-xs text-amber-700">
                        Margen aún positivo, pero el PVP ya se desvía del presupuesto.
                      </p>
                    </div>
                    <div className="flex h-full flex-col rounded-xl border border-rose-200 bg-rose-50 p-5">
                      <p className="text-sm text-rose-700">Sobre presupuesto</p>
                      <div className="flex flex-1 items-center justify-center">
                        <p className="text-center text-5xl font-semibold text-rose-900">
                          {stateSummary.over_budget}
                        </p>
                      </div>
                      <p className="mt-auto pt-2 text-xs text-rose-700">
                        Proyectos cuyo presupuesto ya va por detrás del PVP real.
                      </p>
                    </div>
                  </div>
                )}
              </ChartCard>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
            <ChartCard
              title="Top proyectos por margen"
              subtitle="Los proyectos con mejor rendimiento porcentual dentro del filtro actual."
              loading={overviewLoading}
            >
              {!overview || topProjectsByMargin.length === 0 ? (
                <EmptyState description="No hay proyectos con margen suficiente para mostrar ranking." />
              ) : (
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart data={topProjectsByMargin} layout="vertical" margin={{ left: 12, right: 24 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis type="number" tick={{ fontSize: 12 }} />
                    <YAxis dataKey="name" type="category" width={90} tick={{ fontSize: 12 }} />
                    <Tooltip
                      formatter={(value, name, payload) => {
                        if (name === 'marginPct') {
                          return [`${Number(value).toFixed(1)}%`, 'Margen'];
                        }
                        return [
                          formatCurrencyValue(payload?.payload?.marginAmount ?? 0, {
                            maximumFractionDigits: 0,
                          }),
                          'Margen bruto',
                        ];
                      }}
                    />
                    <Bar dataKey="marginPct" fill="#10b981" radius={[0, 6, 6, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </ChartCard>

            <ChartCard
              title="Subproyectos con mayor desvío de coste"
              subtitle="Los sobrecostes más relevantes según el umbral configurado."
              loading={overviewLoading}
            >
              {!overview || costDeviationData.length === 0 ? (
                <EmptyState description="No hay desvíos de coste relevantes para mostrar con los filtros actuales." />
              ) : (
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart data={costDeviationData} layout="vertical" margin={{ left: 12, right: 24 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis type="number" tick={{ fontSize: 12 }} />
                    <YAxis dataKey="name" type="category" width={120} tick={{ fontSize: 12 }} />
                    <Tooltip formatter={(value) => [`${Number(value).toFixed(1)}%`, 'Desvío']} />
                    <Bar dataKey="deviationPct" fill="#ef4444" radius={[0, 6, 6, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </ChartCard>
          </div>
        </TabsContent>

        <TabsContent value="projects" className="min-w-0 space-y-6">
          <Card className="min-w-0 shadow-sm">
            <CardHeader className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <CardTitle>Ranking financiero por proyecto</CardTitle>
                <p className="text-sm text-gray-500">
                  {totalProjects} proyecto(s) dentro del filtro actual.
                </p>
              </div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <Select value={sortBy} onValueChange={(value) => { setSortBy(value as FinanceProjectsSortBy); setOffset(0); }}>
                  <SelectTrigger className="min-w-[220px]">
                    <SelectValue placeholder="Ordenar por" />
                  </SelectTrigger>
                  <SelectContent>
                    {PROJECT_SORT_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={sortDirection} onValueChange={(value) => { setSortDirection(value as FinanceSortDirection); setOffset(0); }}>
                  <SelectTrigger className="min-w-[160px]">
                    <SelectValue placeholder="Dirección" />
                  </SelectTrigger>
                  <SelectContent>
                    {SORT_DIRECTION_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {projectsError ? (
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertTitle>No pudimos cargar el listado financiero</AlertTitle>
                  <AlertDescription>{projectsError}</AlertDescription>
                </Alert>
              ) : null}

              {projectsLoading ? (
                <FinanceProjectsTableSkeleton />
              ) : projects.length === 0 ? (
                <EmptyState description="No hay proyectos que coincidan con el filtro actual." />
              ) : (
                <div className="w-full">
                  <Table className="table-fixed w-full text-sm [&_th]:box-border [&_td]:box-border">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[8%] px-2 text-left">Ref</TableHead>
                        <TableHead className="w-[14%] px-2 text-left">Proyecto</TableHead>
                        <TableHead className="w-[14%] px-2 text-left">Cliente</TableHead>
                        <TableHead className="w-[8%] px-2 text-center">Estado</TableHead>
                        <TableHead className="w-[8%] px-2 text-center">Presupuesto</TableHead>
                        <TableHead className="w-[6%] px-2 text-center">PVP</TableHead>
                        <TableHead className="w-[6%] px-2 text-center">Coste</TableHead>
                        <TableHead className="w-[7%] px-2 text-center">Margen</TableHead>
                        <TableHead className="w-[7%] px-2 text-center">Desviación</TableHead>
                        <TableHead className="w-[6%] px-1 text-center">
                          <span className="block w-full text-center whitespace-normal leading-tight">
                            Subproyectos
                          </span>
                        </TableHead>
                        <TableHead className="w-[8%] px-1 text-center">
                          <span className="block w-full text-center whitespace-normal leading-tight">
                            Pedidos abiertos
                          </span>
                        </TableHead>
                        <TableHead className="w-[9%] px-2 text-center whitespace-normal leading-tight">
                          Acciones
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {projects.map((project) => {
                        const marginAmount = toNumber(project.gross_margin_amount) ?? 0;
                        const budgetDifference = toNumber(project.budget_difference) ?? 0;
                        return (
                          <TableRow key={project.project_id}>
                            <TableCell className="px-2 font-medium text-gray-900">{project.ref}</TableCell>
                            <TableCell
                              className="px-2 align-middle whitespace-normal break-words"
                              title={project.name}
                            >
                              {project.name}
                            </TableCell>
                            <TableCell
                              className="px-2 align-middle whitespace-normal break-words"
                              title={project.client}
                            >
                              {project.client}
                            </TableCell>
                            <TableCell className="px-2 text-center align-middle">
                              <Badge className="mx-auto" variant={STATUS_BADGE_VARIANTS[project.status]}>
                                {formatProjectStatus(project.status)}
                              </Badge>
                            </TableCell>
                            <TableCell className="px-2 text-center align-middle whitespace-nowrap">
                              {formatCurrencyValue(project.total_budget)}
                            </TableCell>
                            <TableCell className="px-2 text-center align-middle whitespace-nowrap">
                              {formatCurrencyValue(project.total_pvp)}
                            </TableCell>
                            <TableCell className="px-2 text-center align-middle whitespace-nowrap">
                              {formatCurrencyValue(project.total_cost)}
                            </TableCell>
                            <TableCell
                              className={`px-2 text-center align-middle font-medium ${marginAmount >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}
                            >
                              <div className="whitespace-nowrap">
                                {formatCurrencyValue(project.gross_margin_amount)}
                              </div>
                              <div className="text-xs text-gray-500">
                                {formatPercentValue(project.gross_margin_pct)}
                              </div>
                            </TableCell>
                            <TableCell
                              className={`px-2 text-center align-middle whitespace-nowrap font-medium ${budgetDifference >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}
                            >
                              {formatCurrencyValue(project.budget_difference)}
                            </TableCell>
                            <TableCell className="px-2 text-center align-middle">
                              {project.subprojects_count}
                            </TableCell>
                            <TableCell className="px-2 text-center align-middle">
                              {project.open_orders_count}
                            </TableCell>
                            <TableCell className="px-2 text-center align-middle">
                              <Button
                                variant="outline"
                                size="sm"
                                className="mx-auto h-8 w-full max-w-[108px] px-2 text-xs"
                                onClick={() => navigate(project.target_route)}
                              >
                                Ver proyecto
                                <ArrowRight className="ml-1 hidden h-3.5 w-3.5 lg:block" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}

              <div className="flex flex-col gap-3 border-t border-gray-100 pt-4 md:flex-row md:items-center md:justify-between">
                <p className="text-sm text-gray-500">
                  Página {currentPage} de {totalPages}
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setOffset(Math.max(0, offset - pageSize))}
                    disabled={currentPage <= 1 || projectsLoading}
                  >
                    Anterior
                  </Button>
                  <Input
                    value={pageInput}
                    onChange={(event) => setPageInput(event.target.value)}
                    onBlur={commitPageInput}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        commitPageInput();
                      }
                    }}
                    className="w-20 text-center"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setOffset(offset + pageSize)}
                    disabled={currentPage >= totalPages || projectsLoading}
                  >
                    Siguiente
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="watch">
          <DashboardSubprojectsToWatch
            error={watchError}
            items={watchData}
            loading={watchLoading}
            onOpen={(route) => navigate(route)}
            formatPercent={formatPercent}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
